import { Center, Input, Pill, Text } from '@mantine/core';
import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';

interface EditablePillProps {
  value: string;
  label: string;
  onChange: (newLabel: string) => void;
  onRemove: () => void;
  leftSection?: ReactNode;
  missing?: boolean;
}

export const EditablePill = ({
  value,
  label,
  onChange,
  onRemove,
  leftSection,
  missing = false,
}: EditablePillProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const save = () => {
    if (editingValue.trim()) {
      onChange(editingValue.trim());
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setEditingValue(label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <Pill
      withRemoveButton={!isEditing}
      onRemove={!isEditing ? onRemove : undefined}
      bg={missing ? 'red' : undefined}
      c={missing ? 'white' : undefined}
      style={{ maxWidth: '100%' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Center h={'100%'} mt={'auto'} style={{ flex: '0 0 auto' }}>
          {leftSection}
        </Center>
        {/* Editable Label */}
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editingValue}
            onChange={(e) => setEditingValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onBlur={save}
            onClick={(e) => e.stopPropagation()}
            variant="unstyled"
            size="xs"
            styles={{
              input: {
                minWidth: `${Math.max(40, editingValue.length * 6)}px`,
                padding: '0 2px',
                color: 'inherit',
                background: 'transparent',
                height: 'auto',
                minHeight: 'auto',
                caretColor: `var(--mantine-color-blue-7)`,
                lineHeight: 'inherit',
              },
              wrapper: { width: 'auto' },
            }}
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        ) : (
          <Text
            size="xs"
            style={{
              cursor: 'pointer',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            {label}
          </Text>
        )}

        {/* Static Value - only show if label and value are different (user has specified a label) */}
        {label !== value && (
          <Text
            size="xs"
            style={{
              flex: '0 1 auto',
              minWidth: 0,
              opacity: 0.6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            ({value})
          </Text>
        )}
      </div>
    </Pill>
  );
};
